import {findKey} from 'lodash';
import {get, isEmpty, isNil, negate} from 'lodash/fp';
import {createSelector} from 'reselect';

import {CLIENT_TO_CONTACT_REPORT_ENUM} from '/enum/peopleToContact.enum';
import {REPORT_STATE_IS_LOADING} from '/enum/reportState.enum';
import {currentScreen} from '/redux/selector/navigation.selector';
import {getCacheKey, getRecordCacheKey} from '/util/records.util';
import {fromProps, withProps} from '/util/selector.util';
import {getDefaultRecordsSort} from '/util/sort.util';

import {report as reportState} from './root.selector';

export const allReportState = createSelector(
  reportState,
  get('allReportState'),
);

export const allReportData = createSelector(reportState, get('allReportData'));

export const allReportMeta = createSelector(reportState, get('allReportMeta'));

export const allReportRecordData = createSelector(
  reportState,
  get('allReportRecordData'),
);

export const allReportUiFilters = createSelector(
  reportState,
  get('allReportUiFilters'),
);

export const allReportApiFilters = createSelector(
  reportState,
  get('allReportApiFilters'),
);

export const allReportUiSorts = createSelector(
  reportState,
  get('allReportUiSorts'),
);

export const allReportApiSorts = createSelector(
  reportState,
  get('allReportUiSorts'),
);

export const reportStateValue = createSelector(
  fromProps(get('reportName')),
  fromProps(get('reportParams')),
  fromProps(get('stateName')),
  fromProps(get('filtersData')),
  allReportState,
  (reportName, reportParams, stateName, filtersData, allState) => {
    const reportKey = getCacheKey({
      recordsName: reportName,
      recordsParams: !isEmpty(reportParams)
        ? reportParams
        : [
            {
              ...getDefaultRecordsSort({
                recordsName: reportName,
              })?.params,
            },
          ],
      filtersData: filtersData
        ? {
            level: filtersData?.level,
            accountStatus: filtersData?.accountStatus,
            products: filtersData?.products,
            ...(filtersData.rank ? {rank: filtersData.rank} : {}),
            period: filtersData?.period,
            fibc: filtersData?.fibc,
            frontline: filtersData?.frontline,
            ...(filtersData?.cabEarned !== undefined
              ? {cabEarned: filtersData.cabEarned}
              : {}),
            ...(filtersData?.aabEarned !== undefined
              ? {aabEarned: filtersData.aabEarned}
              : {}),
            closeToNextRank: filtersData?.closeToNextRank,
            fqvMin: filtersData?.fqvMin,
            fqvMax: filtersData?.fqvMax,
            newClientsAbove: filtersData?.newClientsAbove,
            newClientsBelow: filtersData?.newClientsBelow,
            qualifiedClientsAbove: filtersData?.qualifiedClientsAbove,
            qualifiedClientsBelow: filtersData?.qualifiedClientsBelow,
            newCoachesAbove: filtersData?.newCoachesAbove,
            newCoachesBelow: filtersData?.newCoachesBelow,
          }
        : undefined,
    });
    return get(`${reportKey}.${stateName}`)(allState);
  },
);

export const reportIsLoadingStateValue = withProps({
  stateName: REPORT_STATE_IS_LOADING,
})(reportStateValue);

export const reportData = createSelector(
  fromProps(get('reportName')),
  fromProps(get('reportParams')),
  fromProps(get('filtersData')),
  allReportData,
  (reportName, reportParams, filtersData, allData) => {
    return get(
      getCacheKey({
        recordsName: reportName,
        recordsParams: reportParams,
        filtersData: filtersData
          ? {
              level: filtersData?.level,
              accountStatus: filtersData?.accountStatus,
              products: filtersData?.products,
              ...(filtersData.rank ? {rank: filtersData.rank} : {}),
              period: filtersData?.period,
              fibc: filtersData?.fibc,
              frontline: filtersData?.frontline,
              ...(filtersData?.cabEarned !== undefined
                ? {cabEarned: filtersData.cabEarned}
                : {}),
              ...(filtersData?.aabEarned !== undefined
                ? {aabEarned: filtersData.aabEarned}
                : {}),
              closeToNextRank: filtersData?.closeToNextRank,
              fqvMin: filtersData?.fqvMin,
              fqvMax: filtersData?.fqvMax,
              newClientsAbove: filtersData?.newClientsAbove,
              newClientsBelow: filtersData?.newClientsBelow,
              qualifiedClientsAbove: filtersData?.qualifiedClientsAbove,
              qualifiedClientsBelow: filtersData?.qualifiedClientsBelow,
              newCoachesAbove: filtersData?.newCoachesAbove,
              newCoachesBelow: filtersData?.newCoachesBelow,
            }
          : undefined,
      }),
    )(allData);
  },
);

export const newReportData = createSelector(
  fromProps(get('newReportName')),
  fromProps(get('reportParams')),
  fromProps(get('filtersData')),
  allReportData,
  (reportName, reportParams, filtersData, allData) =>
    get(
      getCacheKey({
        reportName,
        reportParams,
        filtersData: filtersData
          ? {
              level: filtersData?.level,
              accountStatus: filtersData?.accountStatus,
              products: filtersData?.products,
              ...(filtersData.rank ? {rank: filtersData.rank} : {}),
              period: filtersData?.period,
              fibc: filtersData?.fibc,
              frontline: filtersData?.frontline,
              ...(filtersData?.cabEarned !== undefined
                ? {cabEarned: filtersData.cabEarned}
                : {}),
              ...(filtersData?.aabEarned !== undefined
                ? {aabEarned: filtersData.aabEarned}
                : {}),
              closeToNextRank: filtersData?.closeToNextRank,
              fqvMin: filtersData?.fqvMin,
              fqvMax: filtersData?.fqvMax,
              newClientsAbove: filtersData?.newClientsAbove,
              newClientsBelow: filtersData?.newClientsBelow,
              qualifiedClientsAbove: filtersData?.qualifiedClientsAbove,
              qualifiedClientsBelow: filtersData?.qualifiedClientsBelow,
              newCoachesAbove: filtersData?.newCoachesAbove,
              newCoachesBelow: filtersData?.newCoachesBelow,
            }
          : undefined,
      }),
    )(allData),
);

export const hasReportData = createSelector(reportData, negate(isNil));

export const reportMeta = createSelector(
  fromProps(get('reportName')),
  fromProps(get('reportParams')),
  fromProps(get('filtersData')),
  allReportMeta,
  (reportName, reportParams, filtersData, allMeta) => {
    const reportKey = getCacheKey({
      recordsName: reportName,
      recordsParams: !isEmpty(reportParams)
        ? reportParams
        : [
            {
              ...getDefaultRecordsSort({
                recordsName: reportName,
              })?.params,
            },
          ],
      filtersData: filtersData
        ? {
            level: filtersData?.level,
            accountStatus: filtersData?.accountStatus,
            products: filtersData?.products,
            ...(filtersData.rank ? {rank: filtersData.rank} : {}),
            period: filtersData?.period,
            fibc: filtersData?.fibc,
            frontline: filtersData?.frontline,
            ...(filtersData?.cabEarned !== undefined
              ? {cabEarned: filtersData.cabEarned}
              : {}),
            ...(filtersData?.aabEarned !== undefined
              ? {aabEarned: filtersData.aabEarned}
              : {}),
            closeToNextRank: filtersData?.closeToNextRank,
            fqvMin: filtersData?.fqvMin,
            fqvMax: filtersData?.fqvMax,
            newClientsAbove: filtersData?.newClientsAbove,
            newClientsBelow: filtersData?.newClientsBelow,
            qualifiedClientsAbove: filtersData?.qualifiedClientsAbove,
            qualifiedClientsBelow: filtersData?.qualifiedClientsBelow,
            newCoachesAbove: filtersData?.newCoachesAbove,
            newCoachesBelow: filtersData?.newCoachesBelow,
          }
        : undefined,
    });
    return get(reportKey)(allMeta);
  },
);

export const newReportMeta = createSelector(
  fromProps(get('newReportName')),
  fromProps(get('reportParams')),
  allReportMeta,
  (reportName, reportParams, allMeta) =>
    get(getCacheKey({recordsName: reportName, recordsParams: reportParams}))(
      allMeta,
    ),
);

export const reportRecordData = createSelector(
  fromProps(get('recordsName')),
  fromProps(get('recordsParams')),
  fromProps(get('recordParams')),
  allReportRecordData,
  (recordsName, recordsParams, recordParams, allRecordData) => {
    const reportKey = getCacheKey({
      recordsName,
      recordsParams,
    });
    const recordKey = getRecordCacheKey({reportKey, recordParams});
    return get(`${reportKey}.${recordKey}`)(allRecordData);
  },
);

export const hasReportRecordData = createSelector(
  reportRecordData,
  negate(isNil),
);

// CLIENT TO CONTACT

export const clientsToContactNumberForCurrentTab = createSelector(
  allReportData,
  currentScreen,
  (allData, currentScreenName) => {
    const recordsName = findKey(
      CLIENT_TO_CONTACT_REPORT_ENUM,
      (arg: any) => arg === currentScreenName,
    );
    return get(
      getCacheKey({
        recordsName,
        recordsParams: [
          {
            ...getDefaultRecordsSort({
              recordsName,
            })?.params,
          },
        ],
      }),
    )(allData)?.length;
  },
);

export const clientsToContactReport = createSelector(
  allReportData,
  fromProps(get('reportName')),
  fromProps(get('reportParams')),
  (allData, reportName, reportParams = {}) => {
    return get(
      getCacheKey({
        recordsName: reportName,
        recordsParams: !isEmpty(reportParams)
          ? reportParams
          : [
              {
                ...getDefaultRecordsSort({
                  recordsName: reportName,
                })?.params,
              },
            ],
      }),
    )(allData);
  },
);

//check whether the data has been loaded
export const hasClientsToContactReport = createSelector(
  clientsToContactReport,
  negate(isNil),
);

//check whether we got no records in the API response
export const isEmptyClientsToContactReport = createSelector(
  clientsToContactReport,
  isEmpty,
);

export const isEmptyAllReportState = createSelector(allReportState, isEmpty);
